// Photos and videos in the rendered article. mountReader owns their document lifetime.
//
//   lightPlayers   a YouTube player waits as its poster and a play button; a tap starts it.
//                  A player is the heaviest thing a page holds (megabytes of script, its own
//                  process), and most are never played.
//   enableLightbox a tap on a photo shows the article's photos full screen: swipe between
//                  them, pinch or double-tap to zoom, drag down (or Esc) to close. A photo
//                  grows out of its place in the article and goes back into it, so the
//                  viewer reads as that photo, larger, not as somewhere else. From the
//                  keyboard a photo is a tab stop that opens with Enter or Space; the viewer
//                  takes the focus (Tab stays on Close, the arrows turn the pages, the page
//                  behind is inert) and gives it back to the photo it ended on.
//
// Everything that moves moves by transform, at most once per pointer event, so it stays on
// the compositor.

/* eslint-disable @typescript-eslint/no-explicit-any */
const YT_EMBED = /^https:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/;

/**
 * What a YouTube player says about itself once the page listens to it: playing, paused or ended,
 * and the video's title.
 */
export type PlayerReport = { state: "playing" | "paused" | "ended"; title: string; videoId: string };

/** YouTube's player states: 0 ended, 1 playing, 2 paused. Buffering, unstarted and cued say nothing new. */
const YT_STATE: Record<number, PlayerReport["state"] | undefined> = { 0: "ended", 1: "playing", 2: "paused" };

/**
 * Listens to one YouTube player (its iframe) through the messages its page sends a parent that
 * says it is listening, without loading YouTube's own script. [onReport] hears every change of
 * state or title. Stops by itself when the frame leaves the page.
 */
export function watchPlayer(frame: HTMLIFrameElement, onReport: (r: PlayerReport) => void): () => void {
  const win = frame.ownerDocument.defaultView;
  if (!win) return () => {};
  const videoId = YT_EMBED.exec(frame.getAttribute("src") || "")?.[1] ?? "";
  let state = -1, title = "", heard = false;
  const send = (m: object) => { try { frame.contentWindow?.postMessage(JSON.stringify(m), "*"); } catch { /* the frame is going away */ } };
  const hello = () => { send({ event: "listening", id: 1, channel: "widget" }); send({ event: "command", func: "addEventListener", args: ["onStateChange"] }); };
  const onMessage = (e: any) => {
    if (!frame.isConnected) { dispose(); return; }
    if (e.source !== frame.contentWindow || typeof e.data !== "string") return;
    let m: any;
    try { m = JSON.parse(e.data); } catch { return; }
    if (!m || typeof m !== "object") return;
    heard = true;
    let changed = false;
    if (m.event === "onStateChange" && typeof m.info === "number") {
      if (m.info !== state) { state = m.info; changed = true; }
    } else if ((m.event === "infoDelivery" || m.event === "initialDelivery") && m.info && typeof m.info === "object") {
      const t = m.info.videoData?.title;
      if (typeof t === "string" && t && t !== title) { title = t; changed = true; }
      if (typeof m.info.playerState === "number" && m.info.playerState !== state) { state = m.info.playerState; changed = true; }
    }
    const now = YT_STATE[state];
    if (changed && now) onReport({ state: now, title, videoId });
  };
  win.addEventListener("message", onMessage);
  // YouTube answers only after its own page has loaded, so ask again until it does
  frame.addEventListener("load", hello);
  const retry = win.setInterval(() => { if (!frame.isConnected) dispose(); else if (heard) win.clearInterval(retry); else hello(); }, 500);
  const observer = new MutationObserver(() => { if (!frame.isConnected) dispose(); });
  observer.observe(frame.ownerDocument.documentElement, { childList: true, subtree: true });
  function dispose() {
    win!.clearInterval(retry);
    observer.disconnect();
    win!.removeEventListener("message", onMessage);
    frame.removeEventListener("load", hello);
  }
  return dispose;
}

/** Tells a YouTube player what to do: "playVideo", "pauseVideo". */
export function playerCommand(frame: HTMLIFrameElement, func: string): void {
  try { frame.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: "" }), "*"); } catch { /* the frame is going away */ }
}

/**
 * Pauses what plays in the page except YouTube players: native video and Vimeo. A page that is no
 * longer the one being read must not go on talking (a YouTube video keeps playing: the app's bar
 * controls it).
 */
export function pauseOtherMedia(root: ParentNode): void {
  for (const v of root.querySelectorAll<HTMLVideoElement>("video")) { try { v.pause(); } catch { /* not playable */ } }
  for (const f of root.querySelectorAll<HTMLIFrameElement>("iframe")) {
    if (/\/\/player\.vimeo\.com\//.test(f.getAttribute("src") || "")) {
      try { f.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "*"); } catch { /* the frame is going away */ }
    }
  }
}

/**
 * [report] hears a started YouTube player's state with its frame, so a host (the phone's bar) can
 * show and control it. Without it nothing listens and the player is YouTube's alone.
 */
export function lightPlayers(root: ParentNode, opts?: { report?: (r: PlayerReport, frame: HTMLIFrameElement) => void }): () => void {
  const cleanup: (() => void)[] = [];
  for (const f of root.querySelectorAll<HTMLIFrameElement>("iframe")) {
    const src = f.getAttribute("src") || "";
    const m = YT_EMBED.exec(src);
    if (!m) continue;
    const doc = f.ownerDocument;
    const b = doc.createElement("button");
    b.type = "button";
    b.className = "yt";
    b.setAttribute("aria-label", f.getAttribute("title") && f.getAttribute("title") !== "YouTube video" ? `Play: ${f.getAttribute("title")}` : "Play video");
    const poster = doc.createElement("img");
    poster.alt = "";
    poster.setAttribute("decoding", "async");
    // the sharpest poster; a video without one gets a 120-pixel placeholder, so the smaller one that always exists
    const small = `https://i.ytimg.com/vi/${m[1]}/hqdefault.jpg`;
    poster.addEventListener("load", () => { if (poster.naturalWidth <= 120 && poster.src !== small) poster.src = small; });
    poster.addEventListener("error", () => { if (poster.src !== small) poster.src = small; });
    poster.src = `https://i.ytimg.com/vi/${m[1]}/maxresdefault.jpg`;
    const play = doc.createElement("span");
    play.className = "yt-play";
    b.append(poster, play);
    b.addEventListener("click", (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const frame = doc.createElement("iframe");
      for (const a of Array.from(f.attributes)) if (a.name !== "loading") frame.setAttribute(a.name, a.value);
      const location = doc.defaultView?.location;
      const listen = opts?.report && location && /^https?:$/.test(location.protocol) ? `&enablejsapi=1&origin=${encodeURIComponent(location.origin)}` : "";
      frame.setAttribute("src", `${src}${src.includes("?") ? "&" : "?"}autoplay=1&playsinline=1${listen}`);
      frame.setAttribute("allow", "autoplay; encrypted-media; fullscreen; picture-in-picture");
      b.replaceWith(frame);
      if (listen) cleanup.push(watchPlayer(frame, (r) => opts!.report!(r, frame)));
    });
    f.replaceWith(b);
    cleanup.push(() => { if (b.isConnected) b.replaceWith(f); });
  }
  return () => cleanup.splice(0).forEach((dispose) => dispose());
}

/** The largest picture an image offers (its srcset's widest), for full-screen viewing. */
function largest(img: any): string {
  let url = img.currentSrc || img.src, w = 0;
  for (const part of (img.getAttribute("srcset") || "").split(/,\s+(?=https?:)/)) {
    const [u, d] = part.trim().split(/\s+/);
    const n = parseInt(d || "", 10) || 0;
    if (u && n > w) { w = n; url = u; }
  }
  return url;
}

/** A photo worth showing full screen: not an icon, a player poster, or a link to another page. */
function zoomable(img: any): boolean {
  if (img.classList.contains("tiny") || img.closest(".cmp, .yt, .post-h")) return false;
  const a = img.closest("a[href]");
  if (a && !/\.(?:jpe?g|png|webp|gif|avif)(?:[?#]|$)|\/photos?\//i.test(a.getAttribute("href") || "")) return false;
  const w = img.naturalWidth || Number(img.getAttribute("width")) || 1000;
  return w >= 160;
}

/** What a photo is operated by: its link when it is the picture's link, else the photo itself. */
const controlOf = (img: any): any => img.closest("a[href]") || img;

/**
 * [close] shuts the photos if they are up; [dispose] stops listening (the article is going).
 * A photo opens by a tap or click, or from the keyboard: a loose photo takes a tab stop and opens
 * with Enter or Space, a photo that links to its picture is opened by its link.
 */
export function enableLightbox(root: any, hooks?: { open?: (on: boolean) => void }): { close: () => void; dispose: () => void } {
  const doc = root.ownerDocument || root;
  const win = doc.defaultView;
  for (const img of Array.from(root.querySelectorAll("img")) as any[]) {
    if (!zoomable(img)) continue;
    img.classList.add("zoom");
    if (!img.closest("a[href]")) {
      img.setAttribute("tabindex", "0");
      img.setAttribute("role", "button");
      img.setAttribute("aria-label", img.alt ? `Open photo: ${img.alt}` : "Open photo");
    }
  }
  let close: (immediate?: boolean) => void = () => {};
  // the photo an event is about: the one hit, or the only one inside the link that was activated
  const photoOf = (t: any) => {
    const own = t?.closest?.("img");
    if (own) return own;
    const inside = t?.closest?.("a[href]")?.querySelectorAll("img");
    return inside && inside.length === 1 ? inside[0] : null;
  };
  const show = (img: any) => {
    const photos = (Array.from((img.closest(".prose") || root).querySelectorAll("img")) as any[]).filter(zoomable);
    close = open(doc, win, photos, Math.max(0, photos.indexOf(img)), hooks);
  };
  const tap = (e: any) => {
    const img = photoOf(e.target);
    // a photo inside the open photos is theirs (a tap, a double-tap), never another opening
    if (!img || img.closest(".lightbox") || !zoomable(img)) return;
    e.preventDefault();
    e.stopPropagation();
    show(img);
  };
  // as a native button acts: Enter as it goes down, Space as it comes up (so the key coming up
  // after the photos open, with the focus already in them, is not taken for a press on Close)
  const keyed = (e: any) => {
    const img = e.target?.matches?.("img.zoom[role=button]") ? e.target : null;
    if (!img || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.type === "keydown" && e.key === " ") { e.preventDefault(); return; } // not a scroll
    if ((e.type === "keydown" && e.key === "Enter" && !e.repeat) || (e.type === "keyup" && e.key === " ")) {
      e.preventDefault();
      e.stopPropagation();
      show(img);
    }
  };
  root.addEventListener("click", tap, true);
  root.addEventListener("keydown", keyed, true);
  root.addEventListener("keyup", keyed, true);
  return {
    close: () => close(),
    dispose: () => { root.removeEventListener("click", tap, true); root.removeEventListener("keydown", keyed, true); root.removeEventListener("keyup", keyed, true); close(true); },
  };
}

function open(doc: any, win: any, photos: any[], start: number, hooks?: { open?: (on: boolean) => void }): (immediate?: boolean) => void {
  const timers = new Set<number>();
  const preloads: HTMLImageElement[] = [];
  const later = (fn: () => void, ms: number) => {
    const id = win.setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id); return id;
  };
  const el = (tag: string, cls: string) => { const n = doc.createElement(tag); n.className = cls; return n; };
  const lb = el("div", "lightbox");
  lb.setAttribute("role", "dialog");
  lb.setAttribute("aria-modal", "true");
  lb.setAttribute("aria-label", "Photos");
  const track = el("div", "lightbox-track");
  const bar = el("div", "lightbox-bar");
  const shut = el("button", "lightbox-x");
  shut.type = "button";
  shut.setAttribute("aria-label", "Close");
  shut.textContent = "×";
  lb.append(track, bar, shut);
  const slides = photos.map((from) => {
    const s = el("div", "lightbox-s");
    const im = doc.createElement("img");
    im.alt = from.alt || "";
    im.draggable = false;
    s.append(im);
    track.append(s);
    return { im, from, loaded: false };
  });
  // the rest of the page is out of reach while the photos are up, and the focus is in them
  const rest = (Array.from(doc.body.children) as any[]).filter((n) => !n.hasAttribute("inert"));
  doc.body.append(lb);
  for (const n of rest) n.setAttribute("inert", "");
  shut.focus({ preventScroll: true });
  hooks?.open?.(true);
  const openingFrame = win.requestAnimationFrame(() => lb.classList.add("on"));

  let i = start, z = 1, tx = 0, ty = 0;
  const W = () => lb.clientWidth, H = () => lb.clientHeight;
  const still = !!win.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  // The transform that sets photo [k], as the viewer lays it out (contained, never enlarged),
  // over its place in the article; null when that place is off screen (it then just fades).
  const homeOf = (k: number): string | null => {
    const from = photos[k], r = from.getBoundingClientRect();
    if (still || !r.width || r.bottom < 0 || r.top > win.innerHeight) return null;
    const nw = slides[k]!.im.naturalWidth || from.naturalWidth || r.width, nh = slides[k]!.im.naturalHeight || from.naturalHeight || r.height;
    const fit = Math.min(1, W() / nw, H() / nh), w = nw * fit, h = nh * fit, box = lb.getBoundingClientRect();
    const dx = r.left + r.width / 2 - (box.left + W() / 2), dy = r.top + r.height / 2 - (box.top + H() / 2);
    // a cropped tile (a gallery) is covered, as it was on the page
    return `translate3d(${dx}px,${dy}px,0) scale(${Math.max(r.width / w, r.height / h)})`;
  };
  // the photo in the article stays hidden while it is up here, so it is never in two places
  let lifted: any = null;
  // a photo shown again can be focused at once: left to a transition (the page may give images one),
  // its visibility would still read hidden for the first moment, and a hidden element takes no focus
  const reveal = (n: any) => { n.style.transition = "none"; n.style.visibility = ""; n.getBoundingClientRect(); n.style.transition = ""; };
  const lift = (k: number) => { if (lifted) reveal(lifted); lifted = photos[k]; lifted.style.visibility = "hidden"; };
  // what the browser already has shows at once; the largest version replaces it when it arrives
  const load = (k: number) => {
    const s = slides[k];
    if (!s || s.loaded) return;
    s.loaded = true;
    s.im.src = s.from.currentSrc || s.from.src;
    const big = largest(s.from);
    if (big !== s.im.src) { const pre = new win.Image(); preloads.push(pre); pre.onload = () => { s.im.src = big; }; pre.src = big; }
  };
  const animate = (n: any) => { n.classList.add("anim"); later(() => n.classList.remove("anim"), 320); };
  const place = (dx = 0) => { track.style.transform = `translate3d(${-i * W() + dx}px,0,0)`; };
  const zoom = (smooth = false) => {
    const im = slides[i]!.im;
    if (smooth) animate(im);
    im.style.transform = z === 1 && !tx && !ty ? "" : `translate3d(${tx}px,${ty}px,0) scale(${z})`;
  };
  const caption = () => {
    const cap = photos[i].closest("figure")?.querySelector("figcaption");
    const words = (cap?.textContent || (/^(?:photo|image)?$/i.test(photos[i].alt || "") ? "" : photos[i].alt) || "").replace(/\s+/g, " ").trim();
    bar.textContent = "";
    if (photos.length > 1) { const n = el("span", "lightbox-n"); n.textContent = `${i + 1} / ${photos.length}`; bar.append(n); }
    bar.append(words);
    bar.style.display = photos.length > 1 || words ? "" : "none";
  };
  const go = (k: number, smooth = true) => {
    if (k !== i) { z = 1; tx = 0; ty = 0; zoom(); }
    i = Math.max(0, Math.min(photos.length - 1, k));
    lift(i);
    for (const n of [i, i + 1, i - 1, i + 2]) load(n);
    if (smooth) animate(track);
    place();
    caption();
  };
  // how far a zoomed photo may move before its edge comes away from the screen's
  const clamp = () => {
    const im = slides[i]!.im;
    const mx = Math.max(0, (im.offsetWidth * z - W()) / 2), my = Math.max(0, (im.offsetHeight * z - H()) / 2);
    tx = Math.max(-mx, Math.min(mx, tx));
    ty = Math.max(-my, Math.min(my, ty));
  };

  let closed = false;
  const finish = () => {
    timers.forEach((id) => win.clearTimeout(id)); timers.clear();
    win.cancelAnimationFrame(openingFrame);
    preloads.forEach((image) => { image.onload = null; image.src = ""; });
    lb.remove();
    if (lifted) reveal(lifted);
    for (const n of rest) n.removeAttribute("inert");
    const back = controlOf(photos[i]);
    if (back.isConnected && back.tabIndex >= 0) back.focus();
  };
  const close = (immediate = false) => {
    if (closed) { if (immediate) finish(); return; }
    closed = true;
    win.removeEventListener("keydown", keys, true);
    win.removeEventListener("resize", fit);
    win.clearTimeout(tapTimer);
    hooks?.open?.(false);
    if (immediate) { finish(); return; }
    const home = homeOf(i), im = slides[i]!.im;
    lb.classList.remove("on"); lb.style.backgroundColor = "";
    if (home) { animate(im); im.style.transform = home; } else lb.classList.add("gone");
    later(finish, 280);
  };
  const keys = (e: any) => {
    // while photos are up, keys are theirs, not the reader's (j, k, m…)
    e.stopImmediatePropagation();
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowRight" || e.key === "ArrowLeft") { e.preventDefault(); go(i + (e.key === "ArrowRight" ? 1 : -1)); }
    else if (e.key === "Tab") { e.preventDefault(); shut.focus({ preventScroll: true }); } // the close button is the one stop
    else if (e.repeat && (e.key === "Enter" || e.key === " ")) e.preventDefault(); // a key held down from opening must not press Close
  };
  win.addEventListener("keydown", keys, true);
  shut.addEventListener("click", (e: any) => { e.stopPropagation(); close(); });
  lb.addEventListener("wheel", (e: any) => e.preventDefault(), { passive: false });
  // a turned phone or resized window fits the photo again (let go of on closing, so a closed viewer is not kept)
  const fit = () => { if (!closed) { z = 1; tx = 0; ty = 0; zoom(); place(); } };
  win.addEventListener("resize", fit);

  // gestures: one finger swipes (or pans a zoomed photo, or drags down to close); two pinch
  const pts = new Map<number, { x: number; y: number }>();
  let mode: "" | "swipe" | "down" | "pan" | "pinch" | "done" = "";
  let sx = 0, sy = 0, st = 0, x0 = 0, y0 = 0, z0 = 1, d0 = 1, m0 = { x: 0, y: 0 }, lastTap = 0, tapTimer = 0, base = 0;
  // A finger that lands while the photos are still gliding takes them from where they are, not
  // from where they were going: the glide stops under it and the gesture carries on from there.
  const catchUp = () => {
    base = 0;
    if (track.classList.contains("anim")) {
      const m = new win.DOMMatrix(win.getComputedStyle(track).transform);
      track.classList.remove("anim");
      base = m.m41 + i * W();
      place(base);
    }
    const im = slides[i]!.im;
    if (im.classList.contains("anim")) {
      const m = new win.DOMMatrix(win.getComputedStyle(im).transform);
      im.classList.remove("anim");
      z = m.a; tx = m.e; ty = m.f;
      zoom();
    }
  };
  const dist = (a: any, b: any) => Math.hypot(a.x - b.x, a.y - b.y) || 1;
  const mid = (a: any, b: any) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const center = () => { const r = lb.getBoundingClientRect(); return { x: r.left + r.width / 2, y: r.top + r.height / 2 }; };
  lb.addEventListener("pointerdown", (e: any) => {
    if (closed || e.target.closest(".lightbox-x")) return;
    lb.setPointerCapture(e.pointerId);
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 1) { catchUp(); sx = e.clientX; sy = e.clientY; st = win.performance.now(); x0 = tx; y0 = ty; mode = ""; }
    else if (pts.size === 2) {
      const [a, b] = Array.from(pts.values());
      mode = "pinch"; d0 = dist(a, b); m0 = mid(a, b); z0 = z; x0 = tx; y0 = ty;
      place();
    }
  });
  lb.addEventListener("pointermove", (e: any) => {
    if (!pts.has(e.pointerId)) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (mode === "pinch" && pts.size >= 2) {
      const [a, b] = Array.from(pts.values());
      const m = mid(a, b), c = center();
      z = Math.max(0.85, Math.min(5, z0 * dist(a, b) / d0));
      // the point under the fingers stays under them while they pinch and move
      tx = m.x - c.x - (m0.x - c.x - x0) * (z / z0);
      ty = m.y - c.y - (m0.y - c.y - y0) * (z / z0);
      zoom();
      return;
    }
    if (pts.size !== 1 || mode === "done") return;
    const dx = e.clientX - sx, dy = e.clientY - sy;
    if (!mode) {
      if (Math.hypot(dx, dy) < 8) return;
      mode = z > 1.01 ? "pan" : base || Math.abs(dx) > Math.abs(dy) ? "swipe" : "down";
    }
    if (mode === "pan") { tx = x0 + dx; ty = y0 + dy; clamp(); zoom(); }
    else if (mode === "swipe") {
      // the first and last photos give way only a little: there is nothing beyond them
      const d = base + dx, edge = (d > 0 && i === 0) || (d < 0 && i === photos.length - 1);
      place(edge ? d * 0.3 : d);
    } else if (mode === "down") {
      const k = Math.min(1, Math.abs(dy) / H());
      slides[i]!.im.style.transform = `translate3d(0,${dy}px,0) scale(${1 - k * 0.25})`;
      lb.style.backgroundColor = `rgba(0,0,0,${1 - k * 1.6})`;
    }
  });
  const up = (e: any) => {
    if (!pts.has(e.pointerId)) return;
    pts.delete(e.pointerId);
    const dx = e.clientX - sx, dy = e.clientY - sy, dt = Math.max(1, win.performance.now() - st);
    if (mode === "pinch") {
      if (pts.size === 0) {
        if (z < 1) { z = 1; tx = 0; ty = 0; } else clamp();
        zoom(true);
        mode = "";
      } else mode = "done"; // one finger still down: it lifts before anything else happens
      return;
    }
    if (pts.size) return;
    if (mode === "done") { mode = ""; return; }
    if (mode === "swipe") {
      const v = dx / dt, d = base + dx;
      if ((d < -W() * 0.18 || v < -0.45) && i < photos.length - 1) go(i + 1);
      else if ((d > W() * 0.18 || v > 0.45) && i > 0) go(i - 1);
      else go(i);
    } else if (mode === "down") {
      if (Math.abs(dy) > 110 || Math.abs(dy / dt) > 0.6) close();
      else { lb.style.backgroundColor = ""; zoom(true); }
    } else if (mode === "pan") {
      // a little glide in the direction the finger was going
      tx += (dx / dt) * 80; ty += (dy / dt) * 80;
      clamp(); zoom(true);
    } else if (!mode && base) {
      go(i); // a tap that stopped a glide lets it finish
    } else if (!mode) {
      // a tap: two quick ones zoom in on that spot (or back out); one shows or hides the caption
      const now = win.performance.now();
      if (now - lastTap < 300) {
        win.clearTimeout(tapTimer);
        lastTap = 0;
        if (z > 1.01) { z = 1; tx = 0; ty = 0; }
        else { const c = center(); z = 2.5; tx = (c.x - e.clientX) * 1.5; ty = (c.y - e.clientY) * 1.5; clamp(); }
        zoom(true);
      } else {
        lastTap = now;
        tapTimer = later(() => lb.classList.toggle("bare"), 300);
      }
    }
    mode = "";
  };
  lb.addEventListener("pointerup", up);
  lb.addEventListener("pointercancel", up);

  go(start, false);
  // the photo grows out of its place in the article
  const home = homeOf(start), first = slides[start]!.im;
  if (home) { first.style.transform = home; first.getBoundingClientRect(); animate(first); first.style.transform = ""; }
  return close;
}
